import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { floorplanRooms, zoneColors, zoneLabels } from "../data/floorplan";

export default function FloorPlan({ standalone = true, children, activeZone }) {
  const navigate = useNavigate();
  const [hoveredRoom, setHoveredRoom] = useState(null);

  // --- Edit Mode State ---
  const [editMode, setEditMode] = useState(false);
  const [roomsState, setRoomsState] = useState(floorplanRooms);
  const [draggingRoom, setDraggingRoom] = useState(null);
  const svgRef = useRef(null);

  const getSVGPoint = (e) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    return pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
  };

  const handlePointerDown = (e, room, type) => {
    if (!editMode) return;
    e.stopPropagation();
    e.target.setPointerCapture(e.pointerId);
    const pt = getSVGPoint(e);
    setDraggingRoom({
      id: room.id,
      type,
      startRX: room.x,
      startRY: room.y,
      startRW: room.w,
      startRH: room.h,
      startX: pt.x,
      startY: pt.y,
    });
  };

  const handlePointerMove = (e) => {
    if (!draggingRoom) return;
    const pt = getSVGPoint(e);
    const dx = pt.x - draggingRoom.startX;
    const dy = pt.y - draggingRoom.startY;

    setRoomsState((prev) =>
      prev.map((r) => {
        if (r.id !== draggingRoom.id) return r;
        if (draggingRoom.type === "move") {
          return {
            ...r,
            x: Math.round(draggingRoom.startRX + dx),
            y: Math.round(draggingRoom.startRY + dy),
          };
        } else if (draggingRoom.type === "resize") {
          return {
            ...r,
            w: Math.max(10, Math.round(draggingRoom.startRW + dx)),
            h: Math.max(10, Math.round(draggingRoom.startRH + dy)),
          };
        }
        return r;
      })
    );
  };

  const handlePointerUp = (e) => {
    if (draggingRoom) {
      e.target.releasePointerCapture(e.pointerId);
      setDraggingRoom(null);
    }
  };

  const handleExportJSON = () => {
    const jsonStr = JSON.stringify(roomsState, null, 2);
    console.log(jsonStr);
    navigator.clipboard.writeText(jsonStr).then(() => {
      alert("Coordinates copied to clipboard! Paste them into src/data/floorplan.js");
    });
  };

  const filtered = activeZone
    ? roomsState.filter((r) => r.zone === activeZone)
    : roomsState;
  const dimmed = activeZone
    ? roomsState.filter((r) => r.zone !== activeZone)
    : [];

  return (
    <div className={`${standalone ? "panel section " : ""}floorplan-section`}>
      <div className="section-head" style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", margin: "0" }}>
        <div>
          <h2 style={{ fontSize: "16px", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.05em" }}>Facility Layout</h2>
        </div>
        <div className="btn-row">
          <button className="btn btn-ghost" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", fontSize: "13px" }}>
            <span style={{ marginRight: 6 }}>⬚</span> Legend
          </button>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredRoom && !editMode && (
        <div className="floorplan-tooltip">
          <strong>{hoveredRoom.label}</strong>
          <span>{zoneLabels[hoveredRoom.zone]}</span>
        </div>
      )}

      {/* Floor Plan with PDF background + interactive overlays */}
      <div className="floorplan-container" style={{ position: "relative", display: "flex", gap: "24px", alignItems: "flex-start" }}>
        {editMode && (
          <div style={{ position: "absolute", top: 10, left: 10, background: "rgba(0,0,0,0.8)", color: "white", padding: "10px 14px", borderRadius: 8, zIndex: 10, pointerEvents: "none" }}>
            <strong>Edit Mode Active</strong><br/>
            <small>Drag the center of a room to move it. Drag the bottom-right corner to resize.</small>
          </div>
        )}
        
        {/* Map controls overlay */}
        <div style={{ position: "absolute", bottom: 20, left: 20, display: "flex", gap: "8px", zIndex: 10 }}>
          <div style={{ display: "flex", background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
            <button style={{ padding: "8px 12px", background: "none", border: "none", borderRight: "1px solid #e2e8f0", cursor: "pointer", fontSize: "16px", color: "#64748b" }}>+</button>
            <button style={{ padding: "8px 12px", background: "none", border: "none", borderRight: "1px solid #e2e8f0", cursor: "pointer", fontSize: "16px", color: "#64748b" }}>−</button>
            <button style={{ padding: "8px 12px", background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#64748b" }}>[ ]</button>
          </div>
        </div>

        <div style={{ position: "absolute", bottom: 20, right: 20, zIndex: 10 }}>
          <button style={{ padding: "8px 16px", background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", cursor: "pointer", fontSize: "13px", color: "#0f172a", fontWeight: "500", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", display: "flex", alignItems: "center", gap: "8px" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
            Hide Inactive Rooms
          </button>
        </div>

        <div className="floorplan-stack" style={{ flex: 1 }}>
          <img
            src={process.env.PUBLIC_URL + "/floorplan.png"}
            alt="MTL Cannabis facility floor plan"
            className="floorplan-image"
          />
          <svg
            ref={svgRef}
            viewBox="0 0 2384 3370"
            className="floorplan-overlay-svg"
            xmlns="http://www.w3.org/2000/svg"
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            {/* Dimmed rooms */}
            {dimmed.map((room) => {
              const colors = zoneColors[room.zone];
              return (
                <g key={room.id + "-dim"} opacity="0.15">
                  <rect
                    x={room.x}
                    y={room.y}
                    width={room.w}
                    height={room.h}
                    rx="8"
                    fill={colors.fill}
                    stroke={colors.stroke}
                    strokeWidth="2"
                  />
                </g>
              );
            })}

            {/* Active rooms */}
            {filtered.map((room) => {
              const colors = zoneColors[room.zone];
              const isHovered = hoveredRoom?.id === room.id || (draggingRoom && draggingRoom.id === room.id);
              return (
                <g
                  key={room.id}
                  className="floorplan-room"
                  onClick={() => !editMode && navigate(`/room/${room.id}`)}
                  onMouseEnter={() => setHoveredRoom(room)}
                  onMouseLeave={() => setHoveredRoom(null)}
                  style={{ cursor: editMode ? "move" : "pointer" }}
                >
                  <rect
                    x={room.x}
                    y={room.y}
                    width={room.w}
                    height={room.h}
                    rx="8"
                    fill={isHovered ? colors.hover : colors.fill}
                    stroke={isHovered ? colors.text : colors.stroke}
                    strokeWidth={isHovered || editMode ? 5 : 3}
                    fillOpacity={isHovered || editMode ? 0.55 : 0.35}
                    onPointerDown={(e) => handlePointerDown(e, room, "move")}
                  />
                  <text
                    x={room.x + room.w / 2}
                    y={room.y + room.h / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={colors.text}
                    fontSize={room.w < 120 ? 18 : room.w < 200 ? 22 : 26}
                    fontWeight="700"
                    fontFamily="var(--body)"
                    pointerEvents="none"
                  >
                    {room.label.length > 14 && room.w < 200
                      ? room.label.split(" ").map((word, i, arr) => (
                          <tspan
                            key={i}
                            x={room.x + room.w / 2}
                            dy={i === 0 ? `-${(arr.length - 1) * 12}px` : "26px"}
                          >
                            {word}
                          </tspan>
                        ))
                      : room.label}
                  </text>
                  {/* Resize Handle */}
                  {editMode && (
                    <circle
                      cx={room.x + room.w}
                      cy={room.y + room.h}
                      r="16"
                      fill={colors.text}
                      style={{ cursor: "se-resize" }}
                      onPointerDown={(e) => handlePointerDown(e, room, "resize")}
                    />
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Room count */}
      <div className="floorplan-stats">
        <span>{roomsState.length} rooms mapped</span>
        {activeZone && (
          <span>
            {filtered.length} {zoneLabels[activeZone].toLowerCase()} shown
          </span>
        )}
      </div>
    </div>
  );
}
